import { useEffect, useMemo, useState } from 'react';
import { Flex, Select } from 'antd';
import { ZoneDetailHook } from '../../../../hooks/zone';
import {
  CreateAislePopup,
  CreateRackPopup,
  EditAislePopup,
  RackViewer,
  StaticRackViewer,
  WarehouseSettingsBreadcrumb,
  ZoneDimensionsDetails,
} from '../../../../page-components/zones';
import { useWarehouseDetails } from '../warehouse';
import { CommonUtility } from '../../../../utility';
import { RandomUtility } from '../../../../utility/random';

function ZoneDetailHeader({ zoneId, zoneOptions, onChangeZone, breadcrumbItems }) {
  const selectOptions = zoneOptions?.map(opt => ({
    value: opt.value,
    label: opt.text,
  }));

  return (
    <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
      <WarehouseSettingsBreadcrumb items={breadcrumbItems} />
      <Select
        value={zoneId}
        onChange={onChangeZone}
        options={selectOptions}
        style={{ minWidth: 200 }}
      />
    </Flex>
  );
}

export function ZoneDetailScreen({
  zoneId,
  zoneOptions,
  onChangeZone,
  data,
  refreshData,
  count,
  aloneRacks,
  loading,
  setData,
  setAloneRacks,
  updatePositions,
  updateAisleOrder,
}) {
  const [rackPopup, setRackPopup] = useState(false);
  const [aislePopup, setAislePopup] = useState(false);
  const [aisle, setAisle] = useState(null);
  const [hasIsles, setHasIsles] = useState(false);
  const [editAislePopup, setEditAislePopup] = useState(null);

  const { warehouseData } = useWarehouseDetails();
  const { data: ZoneData, refresh: refreshZoneData } = ZoneDetailHook(zoneId);

  const breadcrumbItems = useMemo(
    () => [
      {
        label: warehouseData?.name ?? 'Warehouse',
        to: '/app/settings/warehouse',
      },
      {
        label: ZoneData?.code ? `${ZoneData.code}: ${ZoneData.type}` : '…',
      },
    ],
    [warehouseData?.name, ZoneData?.code, ZoneData?.type],
  );

  useEffect(() => {
    if (CommonUtility.isValidArray(data)) {
      setHasIsles(true);
    }
  }, [data]);

  const toggleAisles = () => {
    setHasIsles(!hasIsles);
  };

  const openAislePopup = () => {
    setAislePopup(true);
  };

  const closeAislePopup = () => {
    setAislePopup(false);
  };

  const closeRackPopup = () => {
    setRackPopup(false);
    setAisle(null);
  };

  const addRackInAsile = aisle => {
    setAisle(aisle);
    setRackPopup(true);
  };

  const onCloseEditAislePopup = () => {
    setEditAislePopup(null);
  };

  const onClickEditAisle = aisle => {
    setEditAislePopup(aisle);
  };

  const color = useMemo(
    () =>
      ZoneData?.color !== 'rgb(255,255,255)'
        ? ZoneData?.color
        : RandomUtility.generateDarkColor(),
    [ZoneData],
  );

  const showDND = !((count?.racks || 0) > 500);

  return (
    <>
      <ZoneDetailHeader
        zoneId={zoneId}
        zoneOptions={zoneOptions}
        onChangeZone={onChangeZone}
        breadcrumbItems={breadcrumbItems}
      />
      <div className="row">
        <div className="col-4 p-0 mt-2">
          <ZoneDimensionsDetails
            refresh={refreshZoneData}
            detailLoading={loading}
            data={ZoneData}
            hasAisles={hasIsles}
            toggleAisles={toggleAisles}
            count={count}
            color={color}
          />
        </div>
        <div className="col-8 pr-0">
          {showDND ? (
            <RackViewer
              aloneRacks={aloneRacks}
              data={data}
              addRackInAsile={addRackInAsile}
              aisleView={hasIsles}
              loading={loading}
              onClickEditAisle={onClickEditAisle}
              openAislePopup={openAislePopup}
              setData={setData}
              setAloneRacks={setAloneRacks}
              color={color}
              updatePositions={updatePositions}
              updateAisleOrder={updateAisleOrder}
            />
          ) : (
            <StaticRackViewer
              aloneRacks={aloneRacks}
              data={data}
              addRackInAsile={addRackInAsile}
              aisleView={hasIsles}
              loading={loading}
              onClickEditAisle={onClickEditAisle}
              openAislePopup={openAislePopup}
              setData={setData}
              setAloneRacks={setAloneRacks}
              color={color}
              updatePositions={updatePositions}
            />
          )}
        </div>
      </div>
      <CreateRackPopup
        zoneId={zoneId}
        open={rackPopup}
        onClose={closeRackPopup}
        refreshData={refreshData}
        aisle={aisle}
      />
      <CreateAislePopup
        zoneId={zoneId}
        open={aislePopup}
        onClose={closeAislePopup}
        refreshData={refreshData}
      />
      <EditAislePopup
        zoneId={zoneId}
        refreshData={refreshData}
        open={editAislePopup}
        onClose={onCloseEditAislePopup}
        aisle={editAislePopup}
      />
    </>
  );
}
